import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChipSelector from "components/shared/StatusChipSelector";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { ETransferTermSalesStatus } from "store/transferTermSalesStore";

import variables from "../../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

interface IAdvanceTable {
    advancePayments: ITransferTermSale[];
    onAdvanceChange: (status: number, currentRow: ITransferTermSale) => void;
}

/**
 * Table within TransferTermDrawer that displays advance payments of term.
 */
const AdvanceTable = ({ advancePayments, onAdvanceChange }: IAdvanceTable) => {
    const [t] = useTranslation();

    const { termSalesStatus } = useAppSelector((state) => state.transferTerms);

    const paymentRules: Record<any, IStatusRule> = {
        [ETransferTermSalesStatus.PAYED]: {
            color: "green",
            text: t("TransferTerms.SalesStatus.Payed"),
            cssBackgroundColor: variables["success-light"],
            cssIconColor: variables["success"],
        },
        [ETransferTermSalesStatus.WAITING_PAYMENT]: {
            color: "blue",
            text: t("TransferTerms.SalesStatus.WaitingPayment"),
            cssBackgroundColor: variables["info-light"],
            cssIconColor: variables["info"],
        },
        [ETransferTermSalesStatus.LATE_PAYMENT]: {
            color: "red",
            text: t("TransferTerms.SalesStatus.LatePayment"),
            cssBackgroundColor: variables["danger-light"],
            cssIconColor: variables["danger"],
        },
    };

    const totalAdvancePayment = advancePayments.reduce((a, { netAmount }) => a + netAmount, 0) / 100;

    const advanceColumns: LastElementTableColumns[] = [
        {
            id: "",
            label: "",
            format: (value, id, currentRow) => {
                const advanceIndex = advancePayments.indexOf(currentRow);
                return <span>{t("TransferTerms.Drawer.AdvanceTier", { index: advanceIndex + 1 })}</span>;
            },
        },
        {
            id: "status",
            label: t("TransferTerms.Drawer.PaymentStatus"),
            align: "left",
            format: (value: any, id, currentRow) => (
                <div className={styles["status-cell"]}>
                    <StatusChipSelector
                        status={value}
                        statusRules={paymentRules}
                        onChange={(status) => onAdvanceChange(status, currentRow)}
                        width="190px"
                        blockValues={[ETransferTermSalesStatus.PAYED]}
                    />
                </div>
            ),
        },
        {
            id: "paymentDate",
            label: t("TransferTerms.SalesTable.PaymentDate"),
            format: (value) => formatLocaleDateString({ date: value }),
            align: "right",
        },
        {
            id: "grossAmount",
            label: t("TransferTerms.Drawer.GrossAdvance"),
            format: (value) => formatNumber(value / 100, { isCurrency: true }),
            align: "right",
        },
        {
            id: "netAmount",
            label: t("TransferTerms.Drawer.NetAdvance"),
            format: (value) => formatNumber(value / 100, { isCurrency: true }),
            align: "right",
        },
    ];

    const bottomFooterContent = (
        <div className="info-footer">
            <div className={styles["advance-total"]}>
                <span>{t("TransferTerms.Drawer.Total")}</span>
                <span>{formatNumber(totalAdvancePayment ?? 0, { isCurrency: true })}</span>
            </div>
        </div>
    );

    return (
        <LastElementTable
            data={advancePayments}
            columns={advanceColumns}
            bottomFooterContent={bottomFooterContent}
            stickyFooter
            isLoading={termSalesStatus === "pending"}
            dataType="transferTermAdvances"
            columnsNumber={advanceColumns.length}
        />
    );
};

export default AdvanceTable;
